import type { RefObject } from 'react';
import { useCallback, useEffect, useRef } from 'react';

type SelectionRange = {
  starts: number;
  ends: number;
};

function applySelection(input: HTMLInputElement, range: SelectionRange): void {
  if (!input.value) return;

  const end = Math.min(range.ends, input.value.length);
  input.setSelectionRange(range.starts, end);

  /** Only scroll to the end if the highlight extends to
   * (or near) the end of the input. */
  if (end >= input.value.length) {
    const el = input;
    el.scrollLeft = el.scrollWidth;
  }
}

/**
 * Schedules text selection on an input after React updates the controlled value.
 *
 * Handles two scenarios:
 * 1. Input already focused: Applies selection via RAF (after React render)
 * 2. Input not yet focused: Stores pending selection, applies on focus event
 */
export function useInputSelectionOnFocus(
  inputRef: RefObject<HTMLInputElement | null>
) {
  const rafRef = useRef<number | null>(null);
  const pendingSelectionRef = useRef<SelectionRange | null>(null);

  // Apply pending selection when focus arrives (handles async dialog focus)
  useEffect(() => {
    const input = inputRef.current;
    if (!input) return undefined;

    const handleFocus = () => {
      if (pendingSelectionRef.current) {
        const range = pendingSelectionRef.current;

        // Use RAF to ensure React has updated the controlled value
        requestAnimationFrame(() => {
          pendingSelectionRef.current = null;
          if (inputRef.current && document.activeElement === inputRef.current) {
            applySelection(inputRef.current, range);
          }
        });
      }
    };

    input.addEventListener('focus', handleFocus);
    return () => input.removeEventListener('focus', handleFocus);
  }, [inputRef]);

  const scheduleSelection = useCallback(
    (range: SelectionRange) => {
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
      }

      const input = inputRef.current;

      // If input is already focused, apply via RAF immediately.
      // We can't rely on the focus event handler because focus won't fire
      // if the input is already focused (e.g., clicking suggestion cards).
      if (input && document.activeElement === input) {
        rafRef.current = requestAnimationFrame(() => {
          rafRef.current = null;
          if (inputRef.current && document.activeElement === inputRef.current) {
            applySelection(inputRef.current, range);
          }
        });
      } else {
        // Input not focused yet - store for when focus arrives
        pendingSelectionRef.current = range;
      }
    },
    [inputRef]
  );

  // Cleanup RAF on unmount
  useEffect(() => {
    return () => {
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
      }
      pendingSelectionRef.current = null;
    };
  }, []);

  return { scheduleSelection };
}
